import React from 'react';
import { UserPlus, Package, Truck, CheckCircle } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      icon: UserPlus,
      title: 'Sign Up',
      description: 'Create your account in minutes with no setup fees or commitments required.',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: Package,
      title: 'Create Shipment',
      description: 'Enter shipment details, select carrier, and generate shipping labels instantly.',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: Truck,
      title: 'Track & Deliver',
      description: 'Monitor your shipments in real-time and ensure timely delivery to customers.',
      color: 'from-blue-700 to-red-600'
    },
    {
      icon: CheckCircle,
      title: 'Grow Your Business',
      description: 'Focus on growth while we handle the complexities of shipping and logistics.',
      color: 'from-green-500 to-emerald-600'
    }
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden" id="services">
      {/* Decorative Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.02) 2px, transparent 2px), linear-gradient(90deg, rgba(59, 130, 246, 0.02) 2px, transparent 2px)',
          backgroundSize: '60px 60px'
        }}></div>
        
        {/* Gradient orbs */}
        <div className="absolute top-1/4 left-10 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-blue-300/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-10 w-80 h-80 bg-gradient-to-br from-blue-100/30 to-blue-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-flex items-center rounded-full border border-blue-200 bg-white/90 px-4 py-1.5 text-blue-900 font-semibold text-sm uppercase tracking-wide shadow-sm">
            Simple Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mt-4 mb-5 text-gray-900">
            How It{' '}
            <span className="bg-gradient-to-r from-blue-800 to-blue-950 bg-clip-text text-transparent">Works</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
            Launch with Boxd Logistics through a premium, four-step workflow built for speed and control.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7 lg:gap-8">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="relative group">
                {/* Connecting line */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-blue-200 z-0"></div>
                )}

                {/* Step number */}
                <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-blue-700 to-blue-800 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ring-4 ring-white z-20">
                  {index + 1}
                </div>
                
                <div className="relative bg-white/95 backdrop-blur-sm p-8 rounded-2xl border border-blue-100/80 group-hover:border-blue-300 shadow-[0_10px_30px_rgba(15,23,42,0.08)] group-hover:shadow-[0_20px_45px_rgba(29, 78, 216,0.18)] transition-all duration-300 group-hover:-translate-y-1 z-10 overflow-hidden">
                  <div className={`w-16 h-16 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                  
                  {/* Decorative element */}
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;